/**
 * Rechtsschutzversicherung des Kunden (tbl_cases_clients.legalExpenseInsurance*).
 *
 * Kundenfeedback Kanzlei Polz 27.10.2026 (II): „Da brauchen wir die Abfrage
 * ja nein unklar noch zusätzlich bitte. Muss dann auch aus dem Fragebogen
 * heraus übernommen werden!"
 *
 * „RSV vorhanden" (legalExpenseInsuranceExists) hat dieselben Werte wie die
 * Frage im Fragebogen (TriStateButtons): yes | no | maybe (= Unklar), leer =
 * noch nicht angegeben. Bei „Nein" und „Unklar" werden die Detailangaben
 * (Versicherung, Schaden-/Scheinnummer) ausgeblendet und nicht verwendet,
 * bleiben aber gespeichert – wer wieder auf „Ja" stellt, findet sie wieder.
 */

export type RsvExists = 'yes' | 'no' | 'maybe'

type RsvField =
  | 'legalExpenseInsuranceExists'
  | 'legalExpenseInsuranceName'
  | 'legalExpenseInsuranceCaseNumber'
  | 'legalExpenseInsurancePolicyNumber'
  | 'legalExpenseInsuranceCoverage'
  | 'legalExpenseInsuranceCoverageScope'
  | 'legalExpenseInsuranceCoverageOther'

type RsvPatch = Partial<Record<RsvField, string | null>>

interface RsvClient {
  legalExpenseInsuranceExists?: unknown
  legalExpenseInsuranceName?: unknown
  legalExpenseInsuranceCaseNumber?: unknown
  legalExpenseInsurancePolicyNumber?: unknown
}

/** Auswahl „RSV vorhanden" (Cockpit-Kachel, Inline-Bearbeitung). */
export const RSV_EXISTS_OPTIONS: ReadonlyArray<{ value: string, label: string }> = [
  { value: '', label: '— offen —' },
  { value: 'yes', label: 'Ja' },
  { value: 'no', label: 'Nein' },
  { value: 'maybe', label: 'Unklar' },
]

const LABELS: Readonly<Record<RsvExists, string>> = { yes: 'Ja', no: 'Nein', maybe: 'Unklar' }

/**
 * Kundenfeedback Kanzlei Polz 03.11.2026 (XIII): „Bei RSV ergänzen: Deckung
 * erteilt ja/nein/unklar. Bei ‚ja' Pillen zum Anklicken: …"
 *
 * „Deckung erteilt" ist legalExpenseInsuranceCoverage (yes | no | maybe wie
 * „RSV vorhanden"). Der Umfang steht kommagetrennt in
 * legalExpenseInsuranceCoverageScope (VARCHAR 255), der Freitext zu
 * „Sonstiges" in legalExpenseInsuranceCoverageOther.
 */
export const RSV_COVERAGE_SCOPE_OPTIONS = Object.freeze([
  'Außergerichtlich',
  '1. Instanz',
  '2. Instanz',
  '3. Instanz',
  'Widerklage',
  'Selbstständiges Beweisverfahren',
  'Außergerichtliches Gutachten',
  'Sonstiges',
] as const)

export type RsvCoverageScope = (typeof RSV_COVERAGE_SCOPE_OPTIONS)[number]

/** Die Pille, die das Textfeld freischaltet. */
export const RSV_COVERAGE_SCOPE_OTHER: RsvCoverageScope = 'Sonstiges'

const SCOPE_SEPARATOR = ', '

function text (value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * Gespeicherten Wert auf yes | no | maybe bringen; alles andere (leer,
 * „unknown") heißt „noch nicht angegeben" (null). Alte Schreibweisen
 * („Ja"/„Nein"/„Unklar") werden mitgelesen.
 */
export function normRsvExists (value: unknown): RsvExists | null {
  switch (text(value).toLowerCase()) {
    case 'yes': case 'ja': return 'yes'
    case 'no': case 'nein': return 'no'
    case 'maybe': case 'unklar': return 'maybe'
    default: return null
  }
}

/** „Ja" / „Nein" / „Unklar", null wenn noch nichts angegeben ist. */
export function rsvExistsLabel (value: unknown): string | null {
  const v = normRsvExists(value)
  return v ? LABELS[v] : null
}

/**
 * Gelten die Detailangaben? Bei „Nein" und „Unklar" nicht – sie werden dann
 * ausgeblendet und nirgends als RSV des Kunden verwendet (Kachel, Beteiligte,
 * Empfänger), bleiben aber gespeichert.
 */
export function rsvDetailsApply (exists: unknown): boolean {
  const v = normRsvExists(exists)
  return v !== 'no' && v !== 'maybe'
}

/** RSV des Kunden für Anzeige und Kontakte; null ohne (geltende) Versicherung. */
export function clientRsv (client: RsvClient | null | undefined): { name: string, policyNumber: string | null, caseNumber: string | null } | null {
  if (!client || !rsvDetailsApply(client.legalExpenseInsuranceExists)) return null
  const name = text(client.legalExpenseInsuranceName)
  if (!name) return null
  return {
    name,
    policyNumber: text(client.legalExpenseInsurancePolicyNumber) || null,
    caseNumber: text(client.legalExpenseInsuranceCaseNumber) || null,
  }
}

function isCoverageScope (value: string): value is RsvCoverageScope {
  return (RSV_COVERAGE_SCOPE_OPTIONS as readonly string[]).includes(value)
}

/** Nur bekannte Pillen, jede einmal, in der Reihenfolge der Optionen. */
function canonicalScope (items: Iterable<string>): RsvCoverageScope[] {
  const chosen = new Set<string>()
  for (const item of items) {
    const v = text(item)
    if (isCoverageScope(v)) chosen.add(v)
  }
  return RSV_COVERAGE_SCOPE_OPTIONS.filter(option => chosen.has(option))
}

/** Gespeicherte Liste („Außergerichtlich, 1. Instanz") → Pillen; Unbekanntes fällt weg. */
export function parseRsvCoverageScope (value: unknown): RsvCoverageScope[] {
  if (typeof value !== 'string') return []
  return canonicalScope(value.split(','))
}

/** Pillen → gespeicherte Liste; ohne Auswahl null. */
export function serializeRsvCoverageScope (items: readonly string[]): string | null {
  const scope = canonicalScope(items)
  return scope.length > 0 ? scope.join(SCOPE_SEPARATOR) : null
}

/** Pille an-/abwählen – liefert eine neue Liste, das Original bleibt unverändert. */
export function toggleRsvCoverageScope (items: readonly string[], option: string): RsvCoverageScope[] {
  if (!isCoverageScope(option)) return canonicalScope(items)
  const current = new Set<string>(items)
  if (current.has(option)) current.delete(option)
  else current.add(option)
  return canonicalScope(current)
}

/** Ist „Sonstiges" gewählt (Textfeld sichtbar)? */
export function rsvCoverageOtherApplies (items: readonly string[]): boolean {
  return items.includes(RSV_COVERAGE_SCOPE_OTHER)
}

interface RsvSaveForm {
  exists?: unknown
  name?: unknown
  caseNumber?: unknown
  policyNumber?: unknown
  /** „Deckung erteilt" – nur mitgeben, wenn der Dialog die Zeile führt. */
  coverage?: unknown
  coverageScope?: readonly string[]
  coverageOther?: unknown
}

/**
 * Deckungsfelder zum Patch: nur bei geltender RSV und nur, wenn das Formular
 * eine Deckungsangabe führt. Umfang und Sonstiges-Text werden ausschließlich
 * bei „Ja" geschrieben; bei Nein/Unklar/offen bleiben sie, wie sie sind.
 */
function coveragePatch (form: RsvSaveForm): RsvPatch {
  if (!('coverage' in form) || !rsvDetailsApply(form.exists)) return {}
  const coverage = normRsvExists(form.coverage)
  if (coverage !== 'yes') return { legalExpenseInsuranceCoverage: coverage }
  const scope = form.coverageScope ?? []
  return {
    legalExpenseInsuranceCoverage: coverage,
    legalExpenseInsuranceCoverageScope: serializeRsvCoverageScope(scope),
    legalExpenseInsuranceCoverageOther: rsvCoverageOtherApplies(scope) ? (text(form.coverageOther) || null) : null,
  }
}

/**
 * Zu speichernde RSV-Felder aus dem Basisdaten-Dialog. Die gewählte Antwort
 * bleibt stehen; nur wenn noch nichts gewählt ist, heißt eine eingetragene
 * Versicherung „Ja". Detailangaben werden nie verworfen.
 */
export function rsvSavePatch (form: RsvSaveForm): RsvPatch {
  const name = text(form.name)
  return {
    legalExpenseInsuranceExists: normRsvExists(form.exists) ?? (name ? 'yes' : null),
    legalExpenseInsuranceName: name,
    legalExpenseInsuranceCaseNumber: text(form.caseNumber),
    legalExpenseInsurancePolicyNumber: text(form.policyNumber),
    ...coveragePatch(form),
  }
}

/**
 * Folgerung beim Inline-Speichern eines Feldes in der Cockpit-Kachel: Eine
 * eingetragene Versicherung setzt „RSV vorhanden" auf Ja. „Nein"/„Unklar"
 * leeren nichts mehr (bis Kundenfeedback 27.10.2026 (II) leerte „Nein" die
 * Detailangaben). Liefert die zusätzlich zu setzenden Felder (leer, wenn
 * nichts folgt).
 */
export function rsvImplications (field: string, value: unknown): RsvPatch {
  if (field === 'legalExpenseInsuranceName' && text(value)) {
    return { legalExpenseInsuranceExists: 'yes' }
  }
  return {}
}
